import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth } from "./api";

const AuthContext = createContext(null);
const USER_KEY = "aivos_user"; // non-sensitive cached profile for fast UX

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await auth.me();
      setUser(me);
      localStorage.setItem(USER_KEY, JSON.stringify(me));
    } catch (err) {
      // Not authenticated (or session expired) — clear cached profile.
      if (err?.response?.status && err.response.status !== 401) {
        console.error("auth.refresh failed:", err);
      }
      setUser(null);
      localStorage.removeItem(USER_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const data = await auth.login({ email, password });
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const data = await auth.register(payload);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await auth.logout(); } catch (err) { console.error("logout request failed:", err); }
    localStorage.removeItem(USER_KEY);
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
